import React, { useEffect, useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { useNavigate, useParams } from "react-router-dom";
import { DEFAULT_PAGE_SIZE, urlEndPoint } from "helpers/GeneralHelper";

import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Row,
} from "reactstrap";
import Select from "react-select";
import { AsyncClientSelect } from "components/Common/AsyncSearchSelects";
import {
  applyClientAsyncFilterOptionToFormState,
  buildClientAsyncFilterInitialOption,
  getClientAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";
import EcommerceOrdersModal from "components/Common/EcommerceOrdersModal";
import TableContainer from "components/Common/TableContainer";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import {
    fetchClientJobDescriptionTableAndFilterSchema,
    submitClientJobDescriptionFilterData,
    deleteClientJobDescription,
    shortenCommonLink,
} from "model/ClientJobDescriptionModel";
import { fetchCommonJobOptions } from "model/JobModel";
import Loaders from "components/Common/Loaders";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { usePermissionContext } from "libs/context/PermissionContext";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";

const strOrNull = (v) => {
    if (v === null || v === undefined) return null;
    const s = String(v).trim();
    return s === "" ? null : s;
};

const normalizeJobDescriptionFilterPayload = (raw = {}) => {
    const out = { ...raw };
    ["nameContains", "clientId", "jobId"].forEach((key) => {
        if (key in out) out[key] = strOrNull(out[key]);
    });
    return out;
};

const getSelectedOption = (options, value) =>
    (Array.isArray(options) ? options : []).find(
        (opt) => String(opt?.value ?? "") === String(value ?? "")
    ) || null;

const JobDescription = () => {
    const navigate = useNavigate();
    const { permissionData } = usePermissionContext() || {};
    const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize("jobdescription");
    const initialFilterFetchedRef = useRef(false);
    const [modal1, setModal1] = useState(false);
    const toggleViewModal = () => setModal1(!modal1);
    const [filterForm, setFilterForm] = useState({
        search: "",
        clientId: "",
        [getClientAsyncFilterLabelFieldKey()]: "",
        jobId: "",
    });
    const [jobDescriptionData, setJobDescriptionData] = useState([]);
    const [tableMetaData, setTableMetaData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [jobOptions, setJobOptions] = useState([]);
    const [requestPayload, setRequestPayload] = useState({
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        nameContains: null,
        clientId: null,
        jobId: null,
    });
    const [paginationMeta, setPaginationMeta] = useState({
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        totalPages: 0,
        totalRecords: 0,
    });
    const { clientId } = useParams();

    const filteredData = useMemo(() => jobDescriptionData, [jobDescriptionData]);

    const jobDescriptionActionPermissions = useMemo(
        () => ({
            canCreate: Boolean(permissionData?.RegisterJobDescriptionUpdateAccess),
            // Edit opens the upsert form (view/edit); save is gated on the upsert page.
            canEdit: true,
            canDelete: Boolean(permissionData?.RegisterJobDescriptionUpdateAccess),
            canCopy: Boolean(
                permissionData?.ClientJobDescViewAcces ||
                    permissionData?.RegisterJobDescriptionUpdateAccess
            ),
            canShortLink: Boolean(
                permissionData?.ClientJobDescViewAcces ||
                    permissionData?.RegisterJobDescriptionUpdateAccess
            ),
            canDuplicate: Boolean(permissionData?.RegisterJobDescriptionUpdateAccess),
        }),
        [permissionData]
    );

    const hasAnyJobDescriptionRowAction = useMemo(() => {
        const { canCreate, ...rowActions } = jobDescriptionActionPermissions;
        return Object.values(rowActions).some(Boolean);
    }, [jobDescriptionActionPermissions]);

    const fetchSchema = async () => {
        const response = await fetchClientJobDescriptionTableAndFilterSchema({});
        const columns = response?.data?.table?.columns || [];
        setTableMetaData(
            (columns || []).map((col) => ({
                Header: col?.title,
                accessor: col?.field,
                type: col?.type,
            }))
        );
    };

    const fetchFilterOptions = async () => {
        const jobRes = await fetchCommonJobOptions({});
        const jobs =
            jobRes?.data?.records ??
            jobRes?.data?.data ??
            jobRes?.data ??
            jobRes?.records ??
            [];
        const rows = Array.isArray(jobs) ? jobs : [];
        setJobOptions(
            rows
                .map((item) => {
                    const value = String(
                        item?.id ?? item?._id ?? item?.value ?? item?.jobId ?? ""
                    ).trim();
                    const label = String(
                        item?.display ??
                            item?.name ??
                            item?.title ??
                            item?.jobName ??
                            item?.label ??
                            ""
                    ).trim();
                    if (!value) return null;
                    return { value, label: label || value };
                })
                .filter(Boolean)
        );
    };

    const getJobDescriptionList = async (payloadOverrides = {}) => {
        setLoading(true);
        const payload = normalizeJobDescriptionFilterPayload({
            pageNumber: requestPayload.pageNumber,
            pageSize: requestPayload.pageSize,
            nameContains: requestPayload.nameContains,
            clientId: requestPayload.clientId,
            jobId: requestPayload.jobId,
            ...payloadOverrides,
        });
        const response = await submitClientJobDescriptionFilterData(payload);
        const responseData = response?.data || {};
        const listData = responseData?.records || responseData || [];
        const mappedData = (Array.isArray(listData) ? listData : []).map((item) => ({
            ...item,
            id: item?.id || item?._id || "",
        }));

        if (response) {
            setJobDescriptionData(mappedData);
            setPaginationMeta({
                pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
                pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
                totalPages: Number(responseData?.totalPages) || 0,
                totalRecords: Number(responseData?.totalRecords) || 0,
            });
            setRequestPayload(normalizeJobDescriptionFilterPayload(payload));
        } else {
            setJobDescriptionData([]);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchSchema();
        fetchFilterOptions();
    }, []);

    useEffect(() => {
        if (!settingsReady || initialFilterFetchedRef.current) return;
        initialFilterFetchedRef.current = true;
        const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
        setRequestPayload((previous) => ({ ...previous, pageSize }));
        setPaginationMeta((previous) => ({ ...previous, pageSize }));
        getJobDescriptionList({
            pageNumber: 1,
            pageSize,
            ...(clientId ? { clientId } : {}),
        });
    }, [settingsReady, settingsPageSize]);

    const buildPayloadFromUpperForm = (form = {}) =>
        normalizeJobDescriptionFilterPayload({
            pageNumber: 1,
            pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
            nameContains: strOrNull(form?.search),
            clientId: strOrNull(form?.clientId),
            jobId: strOrNull(form?.jobId),
        });

    const handleUpperSearch = () => {
        getJobDescriptionList(buildPayloadFromUpperForm(filterForm));
    };

    const handleFilterFieldChange = (e) => {
        const { name, value } = e.target;
        setFilterForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleJobSelectChange = (selectedOption) => {
        setFilterForm((prev) => ({
            ...prev,
            jobId: selectedOption?.value ?? "",
        }));
    };

    const handlePageChange = async (page) => {
        await getJobDescriptionList({ pageNumber: page });
    };

    const handleRowsPerPageChange = async (rows) => {
        await getJobDescriptionList({ pageNumber: 1, pageSize: rows });
    };

    const handleEdit = (row = {}) => {
        navigate(`/${urlEndPoint.jobDescriptionAddEdit}/${row?.clientId}/${row?.id}`);
    };

    const handleDuplicate = (row = {}) => {
        navigate(`/${urlEndPoint.jobDescriptionAddEdit}/${row?.clientId}/${row?.id}`, {
            state: { duplicate: true },
        });
    };

    const handleAdd = () => {
        if (!clientId) return;
        navigate(`/${urlEndPoint.jobDescriptionAddEdit}/${clientId}/new`);
    };

    const handleDelete = async (row = {}) => {
        const id = String(row?.id || row?._id || "").trim();
        if (!id) {
            toast.error("Job description ID is missing");
            return;
        }
        const swalResult = await Swal.fire({
            title: "Delete job description?",
            text: "Are you sure you want to delete this record?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "OK",
            cancelButtonText: "Cancel",
            reverseButtons: true,
        });
        if (!swalResult?.isConfirmed) return;

        setLoading(true);
        try {
            const res = await deleteClientJobDescription(id);
            if (res?.success !== false && (res?.success || res?.isSuccess || res?.data != null)) {
                toast.success(res?.message || "Deleted successfully");
                await getJobDescriptionList({ pageNumber: paginationMeta?.pageNumber || 1 });
                return;
            }
            toast.error(res?.message || "Unable to delete");
        } finally {
            setLoading(false);
        }
    };

    const copyLinkToastOptions = {
        className: "toast-text-wrap",
        style: {
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            maxWidth: "min(28rem, 92vw)",
        },
        bodyStyle: {
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
        },
    };

    const copyTextToClipboard = async (text, errorMessage) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`copied : ${text}`, copyLinkToastOptions);
        } catch {
            toast.error(errorMessage);
        }
    };

    const handleCopy = async (row = {}) => {
        const jobPortal = String(row?.jobPortal ?? row?.job_portal ?? "").trim();
        if (!jobPortal) {
            toast.error("Job portal link is not available");
            return;
        }
        await copyTextToClipboard(jobPortal, "Unable to copy job portal link");
    };

    const extractShortenedLink = (response = {}) => {
        const payload = response?.data ?? response ?? {};
        if (typeof payload === "string") return payload.trim();
        return String(
            payload?.shortUrl ??
                payload?.shortLink ??
                payload?.url ??
                payload?.link ??
                ""
        ).trim();
    };

    const handleShortLink = async (row = {}) => {
        const jobPortal = String(row?.jobPortal ?? row?.job_portal ?? "").trim();
        if (!jobPortal) {
            toast.error("Job portal link is not available");
            return;
        }

        const response = await shortenCommonLink(jobPortal);
        const shortLink = extractShortenedLink(response);
        if (!shortLink) {
            toast.error(response?.message || "Unable to shorten link");
            return;
        }
        await copyTextToClipboard(shortLink, "Unable to copy short link");
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <EcommerceOrdersModal isOpen={modal1} toggle={toggleViewModal} />
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="fw-bold mb-3">Job Description</h5>

                                {clientId && jobDescriptionActionPermissions.canCreate ? (
                                    <div className="d-flex justify-content-end mb-3">
                                        <Button color="primary" onClick={handleAdd}>
                                            <i className="mdi mdi-plus me-1"></i>
                                            Add Job Description
                                        </Button>
                                    </div>
                                ) : null}
                            </div>
                            <hr />

                            <LegacyFilterToolbar>
                            <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end">
                                <Col xs="12" md="4" className="metadata-filter-field metadata-filter-field--text">
                                    <span className="manage-user-filter-label">Search</span>
                                    <div className="metadata-filter-field__control">
                                    <Input
                                        type="text"
                                        name="search"
                                        placeholder="Search by name"
                                        className={LEGACY_FILTER_INPUT_CLASS}
                                        value={filterForm.search}
                                        onChange={handleFilterFieldChange}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                handleUpperSearch();
                                            }
                                        }}
                                    />
                                    </div>
                                </Col>
                                <Col xs="12" md="3" className="metadata-filter-field metadata-filter-field--autocomplete">
                                    <span className="manage-user-filter-label">Client</span>
                                    <div className="metadata-filter-field__control">
                                    <AsyncClientSelect
                                        key={`job-description-client-${filterForm.clientId}`}
                                        pageName="ClientMenu"
                                        value={filterForm.clientId}
                                        initialOption={buildClientAsyncFilterInitialOption(
                                            filterForm.clientId,
                                            filterForm[getClientAsyncFilterLabelFieldKey()]
                                        )}
                                        onChange={(option) =>
                                            setFilterForm((prev) =>
                                                applyClientAsyncFilterOptionToFormState(prev, option)
                                            )
                                        }
                                        placeholder="Select Client"
                                    />
                                    </div>
                                </Col>
                                <Col xs="12" md="3" className="metadata-filter-field metadata-filter-field--dropdown">
                                    <span className="manage-user-filter-label">Job</span>
                                    <div className="metadata-filter-field__control">
                                    <Select
                                        options={jobOptions}
                                        className="select2-selection"
                                        placeholder="Select Job"
                                        value={getSelectedOption(jobOptions, filterForm.jobId)}
                                        onChange={handleJobSelectChange}
                                        isClearable
                                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                        menuPosition="fixed"
                                        styles={{
                                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                            menu: (base) => ({ ...base, zIndex: 9999 }),
                                        }}
                                    />
                                    </div>
                                </Col>
                                <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--actions">
                                    <span className="manage-user-filter-label" aria-hidden="true">&nbsp;</span>
                                    <div className="metadata-filter-toolbar__actions">
                                    <Button
                                        color="primary"
                                        className="w-100 manage-user-search-btn"
                                        onClick={handleUpperSearch}
                                    >
                                        <i className="fa fa-search me-1"></i>
                                        Search
                                    </Button>
                                    </div>
                                </Col>
                            </Row>
                            </LegacyFilterToolbar>


                            {!loading && tableMetaData?.length ? (
                                <TableContainer
                                    columns={tableMetaData}
                                    data={filteredData}
                                    isActionRequired={hasAnyJobDescriptionRowAction}
                                    onEdit={handleEdit}
                                    onDelete={
                                        jobDescriptionActionPermissions.canDelete
                                            ? handleDelete
                                            : undefined
                                    }
                                    onCopy={
                                        jobDescriptionActionPermissions.canCopy
                                            ? handleCopy
                                            : undefined
                                    }
                                    // onShortLink={
                                    //     jobDescriptionActionPermissions.canShortLink
                                    //         ? handleShortLink
                                    //         : undefined
                                    // }
                                    onDuplicate={
                                        jobDescriptionActionPermissions.canDuplicate
                                            ? handleDuplicate
                                            : undefined
                                    }
                                    isCheckboxLeftSide={false}
                                    isPagination={true}
                                    currentPage={paginationMeta?.pageNumber}
                                    rowsPerPage={paginationMeta?.pageSize}
                                    totalPages={paginationMeta?.totalPages}
                                    totalRecords={paginationMeta?.totalRecords}
                                    onPageChange={handlePageChange}
                                    onPageSizeChange={handleRowsPerPageChange}
                                    pageName={"jobdescription"}
                                />
                            ) : (
                    //             <Card className="border mb-3">
                    //                 <CardBody>
                    //                     <Spinners setLoading={setLoading} />
                    //     </CardBody>
                    // </Card>
                    <Loaders />

                            )}
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    );
};

JobDescription.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(JobDescription);
